import type { MutableRefObject, ReactNode, TransitionEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import type { CompositionContextValue } from '@joyful/utils'
import { createNonNullableContext } from '@joyful/utils'

export const sunGuyTransparentUrl = new URL(
  '../assets/sun_guy_transparent.webm',
  import.meta.url,
).href

export const sunGuyHatUrl = new URL(
  '../assets/sun_guy_hat.png',
  import.meta.url,
).href

const SUN_SIZE = 80
const HAT_WIDTH = 24
const HAT_HEIGHT = 22
const HAT_OFFSET_X = 28
const HAT_OFFSET_Y = 2
const HAT_HIT_TARGET_SIZE = 28
const HAT_CLICK_RADIUS_PX = 80
const HAT_FLY_DURATION_MS = 700
const MAX_SPEED = 1
const STOP_DISTANCE = 60
const IDLE_FRAME_TIME = 0.509
const WALK_RESUME_DISTANCE = STOP_DISTANCE + 5

interface SunGuyState {
  isEnabled: boolean
  isHatEnabled: boolean
}

interface SunGuyActions {
  toggle: () => void
  toggleHat: () => void
}

type SunGuyContextValue = CompositionContextValue<SunGuyState, SunGuyActions>

const SunGuyContext =
  createNonNullableContext<SunGuyContextValue>('SunGuyContext')

export const useSunGuyContext = SunGuyContext.useRequiredContext

export interface SunGuyProviderProps {
  children: ReactNode
}

export function SunGuyProvider({ children }: SunGuyProviderProps) {
  const [state, setState] = useState<SunGuyState>({
    isEnabled: false,
    isHatEnabled: false,
  })
  const stateRef = useRef(state)
  const cancelHatFlightRef = useRef<(() => void) | null>(null)

  stateRef.current = state

  const toggle = () => {
    setState((previous) => {
      if (previous.isEnabled) {
        return { isEnabled: false, isHatEnabled: false }
      }

      return { ...previous, isEnabled: true }
    })
  }

  const toggleHat = () => {
    const willEnableHat = !stateRef.current.isHatEnabled
    if (willEnableHat) {
      cancelHatFlightRef.current?.()
    }

    setState((previous) => {
      if (previous.isHatEnabled) {
        return { ...previous, isHatEnabled: false }
      }

      return { isEnabled: true, isHatEnabled: true }
    })
  }

  const removeHat = () => {
    setState((previous) => {
      if (!previous.isHatEnabled) {
        return previous
      }

      return { ...previous, isHatEnabled: false }
    })
  }

  return (
    <SunGuyContext.Context value={{ actions: { toggle, toggleHat }, state }}>
      {children}
      {state.isEnabled ? (
        <SunGuyAnimation
          cancelHatFlightRef={cancelHatFlightRef}
          isHatEnabled={state.isHatEnabled}
          onRemoveHat={removeHat}
        />
      ) : null}
    </SunGuyContext.Context>
  )
}

interface SunGuyAnimationProps {
  cancelHatFlightRef: MutableRefObject<(() => void) | null>
  isHatEnabled: boolean
  onRemoveHat: () => void
}

function SunGuyAnimation({
  cancelHatFlightRef,
  isHatEnabled,
  onRemoveHat,
}: SunGuyAnimationProps) {
  const positionRef = useRef({ x: -SUN_SIZE, y: 0 })
  const targetRef = useRef({ x: 0, y: 0 })
  const isFlippedRef = useRef(false)
  const isWalkingRef = useRef(false)
  const isHatEnabledRef = useRef(isHatEnabled)
  const isHatFlyingAwayRef = useRef(false)
  const hatFlightGenerationRef = useRef(0)
  const activeHatFlightGenerationRef = useRef<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const rafRef = useRef<number>(0)
  const [isHatFlyingAway, setIsHatFlyingAway] = useState(false)
  const [prefersReducedMotion] = useState(() => {
    if (typeof window === 'undefined') {
      return false
    }

    if (typeof window.matchMedia !== 'function') {
      return false
    }

    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  })

  isHatEnabledRef.current = isHatEnabled
  isHatFlyingAwayRef.current = isHatFlyingAway

  const isHatVisible = isHatEnabled || isHatFlyingAway

  const invalidateHatFlightRef = useRef(() => {})
  invalidateHatFlightRef.current = () => {
    hatFlightGenerationRef.current += 1
    activeHatFlightGenerationRef.current = null
    setIsHatFlyingAway(false)
  }

  useEffect(() => {
    cancelHatFlightRef.current = () => {
      invalidateHatFlightRef.current()
    }
    return () => {
      cancelHatFlightRef.current = null
    }
  }, [cancelHatFlightRef])

  const handleHatTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
    if (event.propertyName !== 'opacity') {
      return
    }

    if (!isHatFlyingAway) {
      return
    }

    const flightGeneration = activeHatFlightGenerationRef.current
    activeHatFlightGenerationRef.current = null
    if (flightGeneration === null) {
      setIsHatFlyingAway(false)
      return
    }

    if (hatFlightGenerationRef.current !== flightGeneration) {
      setIsHatFlyingAway(false)
      return
    }

    if (!isHatEnabledRef.current) {
      setIsHatFlyingAway(false)
      return
    }

    onRemoveHat()
    setIsHatFlyingAway(false)
  }

  useEffect(() => {
    if (prefersReducedMotion || typeof window === 'undefined') {
      return
    }

    function getHatScreenCenter() {
      const position = positionRef.current
      const containerLeft = position.x - SUN_SIZE / 2
      const containerTop = position.y - SUN_SIZE / 2
      const originX = SUN_SIZE / 2
      let hatLocalCenterX = HAT_OFFSET_X + HAT_WIDTH / 2
      const hatLocalCenterY = HAT_OFFSET_Y + HAT_HEIGHT / 2
      if (isFlippedRef.current) {
        hatLocalCenterX = originX * 2 - hatLocalCenterX
      }

      return {
        x: containerLeft + hatLocalCenterX,
        y: containerTop + hatLocalCenterY,
      }
    }

    function handleHatPointerDown(event: PointerEvent) {
      if (!isHatEnabledRef.current || isHatFlyingAwayRef.current) {
        return
      }

      if (!event.isPrimary) {
        return
      }

      if (event.button !== 0) {
        return
      }

      const hatCenter = getHatScreenCenter()
      const distanceX = event.clientX - hatCenter.x
      const distanceY = event.clientY - hatCenter.y
      const clickRadiusSquared = HAT_CLICK_RADIUS_PX * HAT_CLICK_RADIUS_PX
      const distanceSquared = distanceX * distanceX + distanceY * distanceY
      if (distanceSquared > clickRadiusSquared) {
        return
      }

      const flightGeneration = hatFlightGenerationRef.current
      activeHatFlightGenerationRef.current = flightGeneration
      setIsHatFlyingAway(true)
    }

    window.addEventListener('pointerdown', handleHatPointerDown, true)
    return () => {
      window.removeEventListener('pointerdown', handleHatPointerDown, true)
    }
  }, [prefersReducedMotion])

  useEffect(() => {
    if (prefersReducedMotion || typeof window === 'undefined') {
      return
    }

    positionRef.current = { x: -SUN_SIZE, y: window.innerHeight / 2 }
    targetRef.current = {
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    }
    function handleMouseMove(event: MouseEvent) {
      targetRef.current = { x: event.clientX, y: event.clientY }
    }

    function setWalking(isWalking: boolean) {
      if (isWalkingRef.current === isWalking) {
        return
      }

      isWalkingRef.current = isWalking
      const video = videoRef.current
      if (!video) {
        return
      }

      if (isWalking) {
        void video.play().catch(() => {
          isWalkingRef.current = false
        })
        return
      }

      video.pause()
      video.currentTime = IDLE_FRAME_TIME
    }

    function animate() {
      const position = positionRef.current
      const target = targetRef.current

      const distanceX = target.x - position.x
      const distanceY = target.y - position.y
      const distance = Math.sqrt(distanceX * distanceX + distanceY * distanceY)

      if (distance > STOP_DISTANCE) {
        const travelDistance = distance - STOP_DISTANCE
        const speed = Math.min(travelDistance * 0.08, MAX_SPEED)
        const normalizedX = distanceX / distance
        const normalizedY = distanceY / distance
        position.x += normalizedX * speed
        position.y += normalizedY * speed
      }

      setWalking(distance > WALK_RESUME_DISTANCE)

      const shouldFlip = target.x < position.x - 1
      const shouldUnflip = target.x > position.x + 1
      if (shouldFlip) {
        isFlippedRef.current = true
      }

      if (shouldUnflip) {
        isFlippedRef.current = false
      }

      const container = containerRef.current
      if (container) {
        const translateX = position.x - SUN_SIZE / 2
        const translateY = position.y - SUN_SIZE / 2
        const scaleX = isFlippedRef.current ? -1 : 1
        container.style.transform = `translate(${translateX}px, ${translateY}px) scaleX(${scaleX})`
      }

      rafRef.current = requestAnimationFrame(animate)
    }

    window.addEventListener('mousemove', handleMouseMove)
    rafRef.current = requestAnimationFrame(animate)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      cancelAnimationFrame(rafRef.current)
    }
  }, [prefersReducedMotion])

  if (prefersReducedMotion) {
    return null
  }

  return (
    <div
      ref={containerRef}
      className="pointer-events-none fixed top-0 left-0 z-50 overflow-visible"
      style={{
        height: SUN_SIZE,
        width: SUN_SIZE,
      }}
    >
      <video
        ref={videoRef}
        src={sunGuyTransparentUrl}
        autoPlay
        loop
        muted
        playsInline
        className="size-full object-contain"
      />
      {isHatVisible ? (
        <div
          onTransitionEnd={handleHatTransitionEnd}
          className="absolute"
          style={{
            height: HAT_HIT_TARGET_SIZE,
            left: HAT_OFFSET_X - (HAT_HIT_TARGET_SIZE - HAT_WIDTH) / 2,
            opacity: isHatFlyingAway ? 0 : 1,
            top: HAT_OFFSET_Y - (HAT_HIT_TARGET_SIZE - HAT_HEIGHT) / 2,
            transform: isHatFlyingAway
              ? 'translate(36px, -140px) rotate(28deg) scale(1.2)'
              : 'translate(0, 0) rotate(0deg) scale(1)',
            transition: `opacity ${HAT_FLY_DURATION_MS}ms ease-out, transform ${HAT_FLY_DURATION_MS}ms ease-out`,
            width: HAT_HIT_TARGET_SIZE,
          }}
        >
          <img
            src={sunGuyHatUrl}
            alt=""
            draggable={false}
            className="absolute"
            style={{
              height: HAT_HEIGHT,
              left: (HAT_HIT_TARGET_SIZE - HAT_WIDTH) / 2,
              top: (HAT_HIT_TARGET_SIZE - HAT_HEIGHT) / 2,
              width: HAT_WIDTH,
            }}
          />
        </div>
      ) : null}
    </div>
  )
}
